import { dateFormatStore } from 'i18n/dateFormatStore';
import { useSnackbar } from 'notistack';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BroadbeanResponse } from 'types/broadbean';
import { formatDateLong } from 'utils/date';

import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import {
  BroadbeanJobStatusEnum,
  Job,
  useSubmitJobToBroadbeanMutation,
} from '@libs/graphql-types';

const useStyles = makeStyles((theme: any) => ({
  root: {
    backgroundColor: '#fff',
    border: '1px solid rgba(25, 25, 25, 0.08)',
    borderRadius: 4,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  editButton: {
    textTransform: 'uppercase',
    fontSize: '0.8125rem',
    letterSpacing: '0.46px',
    borderRadius: 6,
  },
  statusBadge: {
    borderRadius: 15,
    fontWeight: 400,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
  },
  postedBadge: {
    backgroundColor: '#4CAF50',
    color: '#fff',
  },
  draftBadge: {
    backgroundColor: '#FF9800',
    color: '#fff',
  },
  label: {
    fontWeight: 500,
    fontSize: '0.875rem',
    letterSpacing: '0.1px',
    color: theme.palette.text.primary,
  },
  value: {
    fontSize: '0.875rem',
    letterSpacing: '0.17px',
    color: theme.palette.text.primary,
  },
  jobBoardChip: {
    backgroundColor: '#B9F3FA',
    borderRadius: 16,
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
}));

interface BroadbeanPostingCardProps {
  jobData: Job;
  refetch?: () => void;
}

export const BroadbeanPostingCard: React.FC<BroadbeanPostingCardProps> = ({
  jobData,
  refetch,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('job');
  const { enqueueSnackbar } = useSnackbar();
  const [submitToBroadbean, { loading }] = useSubmitJobToBroadbeanMutation();
  const dateLocale = dateFormatStore.get();

  const isPosted = jobData.broadbean_status === BroadbeanJobStatusEnum.Posted;
  const isDraft = jobData.broadbean_status === BroadbeanJobStatusEnum.Draft;

  const broadbeanData = useMemo(() => {
    const response = jobData.broadbean_response as BroadbeanResponse | null;
    if (!response?.status_check_response) return null;

    const { advert, channel_list } = response.status_check_response;
    const channels = channel_list?.channel ?? [];
    const firstChannel = channels[0]?.channel_status;

    return {
      consultant: advert?.consultant ?? null,
      channelNames: channels.map((ch) => ch.channel_name),
      postedTime: firstChannel?.posted_time ?? null,
      removalTime: firstChannel?.removal_time ?? null,
      totalResponses: channels.reduce(
        (sum, ch) => sum + (ch.channel_status?.responses ?? 0),
        0,
      ),
    };
  }, [jobData.broadbean_response]);

  const handleEdit = async () => {
    try {
      const { data } = await submitToBroadbean({
        variables: { job_id: jobData.id },
      });
      const url = data?.submitJobToBroadbean?.broadbean_url;
      if (url) {
        window.open(url, '_blank');
      }
      if (refetch) await refetch();
    } catch {
      enqueueSnackbar(t('v2.broadbeanError', 'Failed to submit to Broadbean'), {
        variant: 'error',
      });
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title}>
          {t('v2.broadbean.title', 'Broadbean posting')}
        </Typography>
        <Button
          variant="outlined"
          color="info"
          size="small"
          className={classes.editButton}
          onClick={handleEdit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {t('v2.edit')}
        </Button>
      </Box>

      {isPosted && (
        <Chip
          label={t('v2.broadbean.posted', 'Posted on job boards')}
          className={`${classes.statusBadge} ${classes.postedBadge}`}
        />
      )}
      {isDraft && (
        <Chip
          label={t('v2.broadbean.pending', 'Pending')}
          className={`${classes.statusBadge} ${classes.draftBadge}`}
        />
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.label}>
            {t('v2.broadbean.supervisor', 'Broadbean supervisor:')}
          </Typography>
          <Typography className={classes.value}>
            {broadbeanData?.consultant ?? '--'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.label}>
            {t('v2.broadbean.removalDate', 'Job removal date:')}
          </Typography>
          <Typography className={classes.value}>
            {broadbeanData?.removalTime
              ? formatDateLong(broadbeanData.removalTime, dateLocale)
              : '--'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.label}>
            {t('v2.broadbean.postingDate', 'Job posting date:')}
          </Typography>
          <Typography className={classes.value}>
            {broadbeanData?.postedTime
              ? formatDateLong(broadbeanData.postedTime, dateLocale)
              : '--'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.label}>
            {t('v2.broadbean.externalBoards', 'Posted on External Job Boards:')}
          </Typography>
          {broadbeanData?.channelNames.length ? (
            <Box display="flex" flexWrap="wrap">
              {broadbeanData.channelNames.map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  className={classes.jobBoardChip}
                />
              ))}
            </Box>
          ) : (
            <Typography className={classes.value}>--</Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.label}>
            {t(
              'v2.broadbean.candidatesApplied',
              'Candidates applied via Broadbean:',
            )}
          </Typography>
          <Typography className={classes.value}>
            {broadbeanData ? broadbeanData.totalResponses : '--'}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};
